// src/lib/youtube.ts

import axios from 'axios';
import { handleAPIError, APIError, ErrorMessages } from './errorHandling';
import { rateLimiters, withRateLimit } from './rateLimiter';
import { withRetry } from './retryLogic';
import { logger } from './logger';

// --------- API KEYS & BASE URL ---------
const API_KEYS = [
  import.meta.env?.VITE_YT_API_KEY_1 || process.env.VITE_YT_API_KEY_1,
  import.meta.env?.VITE_YT_API_KEY_2 || process.env.VITE_YT_API_KEY_2,
  import.meta.env?.VITE_YT_API_KEY_3 || process.env.VITE_YT_API_KEY_3,
  import.meta.env?.VITE_YT_API_KEY_4 || process.env.VITE_YT_API_KEY_4,
].filter(Boolean);

export const BASE_URL = import.meta.env?.VITE_YOUTUBE_API_BASE_URL || process.env.VITE_YOUTUBE_API_BASE_URL;

let currentKeyIndex = 0;
export function getNextApiKey() {
  const key = API_KEYS[currentKeyIndex];
  currentKeyIndex = (currentKeyIndex + 1) % API_KEYS.length;
  return key;
}

if (!API_KEYS.length || !BASE_URL) {
  console.error('❌ Missing YouTube API Keys or BASE_URL.');
}

// --------- CHANNEL DETAILS ---------
export async function getChannelDetails(channelId: string) {
  logger.info(`Fetching channel details for ${channelId}`);
  return withRetry(async () =>
    withRateLimit(rateLimiters.channels, async () => {
      try {
        const url = `${BASE_URL}/channels?part=snippet,statistics&id=${channelId}&key=${getNextApiKey()}`;
        const res = await fetch(url);
        if (!res.ok) throw new APIError('Failed to fetch channel details', res.status);
        const data = await res.json();
        const item = data.items?.[0];
        if (!item) throw new APIError(ErrorMessages.NOT_FOUND, 404);
        return {
          id: item.id,
          title: item.snippet.title,
          subscriberCount: +item.statistics.subscriberCount,
          viewCount: +item.statistics.viewCount,
        };
      } catch (error) {
        throw handleAPIError(error);
      }
    }, 1)
  );
}

// --------- PLAYLIST ID ---------
export async function getUploadsPlaylistId(channelId: string): Promise<string> {
  logger.info(`Fetching uploads playlist ID for ${channelId}`);
  return withRetry(async () =>
    withRateLimit(rateLimiters.channels, async () => {
      try {
        const url = `${BASE_URL}/channels?part=contentDetails&id=${channelId}&key=${getNextApiKey()}`;
        const res = await fetch(url);
        if (!res.ok) throw new APIError('Failed to fetch channel contentDetails', res.status);
        const data = await res.json();
        const item = data.items?.[0];
        if (!item) throw new APIError(ErrorMessages.NOT_FOUND, 404);
        return item.contentDetails.relatedPlaylists.uploads;
      } catch (error) {
        throw handleAPIError(error);
      }
    }, 1)
  );
}

// --------- PAGINATED PLAYLIST VIDEOS (ALL PAGES) ---------
export async function fetchAllPlaylistVideos(playlistId: string): Promise<any[]> {
  let videos: any[] = [];
  let nextPageToken = '';
  do {
    const params = new URLSearchParams({
      part: 'snippet,contentDetails',
      playlistId,
      maxResults: '50',
      key: getNextApiKey(),
    });
    if (nextPageToken) params.set('pageToken', nextPageToken);
    const res = await fetch(`${BASE_URL}/playlistItems?${params.toString()}`);
    if (!res.ok) throw new APIError('Failed to fetch playlist videos', res.status);
    const data = await res.json();
    videos = videos.concat(data.items || []);
    nextPageToken = data.nextPageToken || '';
  } while (nextPageToken);
  return videos;
}

// --------- ALL CHANNEL VIDEOS (with TWO types ONLY: video or short) ---------
export async function getAllChannelVideos(uploadsPlaylistId: string) {
  const playlistVideos = await fetchAllPlaylistVideos(uploadsPlaylistId);
  const videoIds = playlistVideos.map(v => v.snippet.resourceId.videoId);

  // Fetch durations, real YouTube channel name, and view counts
  const metadata = await getVideoMetadata(videoIds);

  return playlistVideos.map((item: any) => {
    const id = item.snippet.resourceId.videoId;
    const meta = metadata[id];
    const duration = meta?.duration ?? 0;
    // Portrait aspect ratio (9:16) = short; everything else = video
    const type: 'short' | 'video' = meta?.isPortrait ? 'short' : 'video';
    return {
      id,
      title: item.snippet.title,
      thumbnail: item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default?.url || '',
      duration,
      type,
      // Real YouTube metadata
      channelName: meta?.channelTitle || item.snippet.videoOwnerChannelTitle || item.snippet.channelTitle || '',
      viewCount: meta?.viewCount ?? 0,
      publishedAt: meta?.publishedAt || item.snippet.publishedAt || '',
    };
  });
}

// --------- SINGLE-PAGE PLAYLIST VIDEOS (LEGACY) ---------
export async function getPlaylistVideos(playlistId: string, pageToken = '') {
  logger.info(`Fetching videos for playlist ${playlistId}`, { pageToken });
  return withRetry(async () =>
    withRateLimit(rateLimiters.videos, async () => {
      try {
        const params = new URLSearchParams({
          part: 'snippet',
          playlistId,
          maxResults: '50',
          key: getNextApiKey(),
        });
        if (pageToken) params.set('pageToken', pageToken);
        const res = await fetch(`${BASE_URL}/playlistItems?${params.toString()}`);
        if (!res.ok) throw new APIError('Failed to fetch playlist videos', res.status);
        const data = await res.json();
        return data.items.map((item: any) => ({
          id: item.snippet.resourceId.videoId,
          title: item.snippet.title,
          thumbnail: item.snippet.thumbnails.medium.url,
        }));
      } catch (error) {
        throw handleAPIError(error);
      }
    }, 1)
  );
}

// --------- VIDEO DURATIONS ---------
function parseISODuration(duration: string): number {
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = duration.match(regex);
  const hours = parseInt(matches?.[1] || '0', 10);
  const minutes = parseInt(matches?.[2] || '0', 10);
  const seconds = parseInt(matches?.[3] || '0', 10);
  return hours * 3600 + minutes * 60 + seconds;
}

export interface YouTubeVideoMeta {
  duration: number;
  channelTitle: string;
  channelId: string;
  viewCount: number;
  publishedAt: string;
  /** True when the video's native aspect ratio is portrait (9:16). */
  isPortrait: boolean;
}

export async function getVideoMetadata(videoIds: string[]): Promise<Record<string, YouTubeVideoMeta>> {
  const metadata: Record<string, YouTubeVideoMeta> = {};
  const chunks = [];
  for (let i = 0; i < videoIds.length; i += 50) {
    chunks.push(videoIds.slice(i, i + 50));
  }
  for (const chunk of chunks) {
    const res = await fetch(
      `${BASE_URL}/videos?part=contentDetails,snippet,statistics,player&id=${chunk.join(',')}&key=${getNextApiKey()}`
    );
    const json = await res.json();
    for (const item of json.items || []) {
      const embedW = item.player?.embedWidth ?? 16;
      const embedH = item.player?.embedHeight ?? 9;
      metadata[item.id] = {
        duration: parseISODuration(item.contentDetails.duration),
        channelTitle: item.snippet?.channelTitle || '',
        channelId: item.snippet?.channelId || '',
        viewCount: parseInt(item.statistics?.viewCount || '0', 10),
        publishedAt: item.snippet?.publishedAt || '',
        isPortrait: Number(embedH) > Number(embedW),
      };
    }
  }
  return metadata;
}

/** Fetch channel avatar URLs for a list of YouTube channel IDs (batched by 50) */
export async function getChannelAvatars(channelIds: string[]): Promise<Record<string, string>> {
  const avatars: Record<string, string> = {};
  const unique = [...new Set(channelIds.filter(Boolean))];
  if (unique.length === 0) return avatars;

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 50) {
    chunks.push(unique.slice(i, i + 50));
  }
  for (const chunk of chunks) {
    try {
      const res = await fetch(
        `${BASE_URL}/channels?part=snippet&id=${chunk.join(',')}&key=${getNextApiKey()}`
      );
      const json = await res.json();
      for (const item of json.items || []) {
        avatars[item.id] =
          item.snippet?.thumbnails?.default?.url ||
          item.snippet?.thumbnails?.medium?.url ||
          '';
      }
    } catch {
      // Silently skip failed batches — avatars are non-critical
    }
  }
  return avatars;
}

// --------- COMMENTS ---------
interface Comment {
  id: string;
  author: string;
  text: string;
  publishedAt: string;
}

type CommentFetchStatus = 'ok' | 'unavailable' | 'error';

const COMMENTS_UNAVAILABLE_TTL_MS = 20 * 60 * 1000;
const COMMENTS_UNAVAILABLE_KEY_PREFIX = 'vb_yt_comments_unavailable_';
const commentsUnavailableCache = new Map<string, number>();
const commentsLogOnce = new Set<string>();

function getCachedUnavailable(videoId: string): boolean {
  const now = Date.now();
  const mem = commentsUnavailableCache.get(videoId);
  if (mem && now - mem < COMMENTS_UNAVAILABLE_TTL_MS) return true;

  if (typeof window !== 'undefined' && window.sessionStorage) {
    const raw = window.sessionStorage.getItem(`${COMMENTS_UNAVAILABLE_KEY_PREFIX}${videoId}`);
    if (raw) {
      const ts = Number(raw);
      if (Number.isFinite(ts) && now - ts < COMMENTS_UNAVAILABLE_TTL_MS) {
        commentsUnavailableCache.set(videoId, ts);
        return true;
      }
      window.sessionStorage.removeItem(`${COMMENTS_UNAVAILABLE_KEY_PREFIX}${videoId}`);
    }
  }
  return false;
}

function markUnavailable(videoId: string) {
  const ts = Date.now();
  commentsUnavailableCache.set(videoId, ts);
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.setItem(`${COMMENTS_UNAVAILABLE_KEY_PREFIX}${videoId}`, String(ts));
  }
}

function getHttpStatus(error: any): number | null {
  if (!error) return null;
  if (typeof error.status === 'number') return error.status;
  if (typeof error.response?.status === 'number') return error.response.status;
  return null;
}

function logOnce(videoId: string, level: 'info' | 'warn', message: string) {
  if (!import.meta.env?.DEV) return;
  if (commentsLogOnce.has(videoId)) return;
  commentsLogOnce.add(videoId);
  logger.info(level === 'warn' ? `[warn] ${message}` : message);
}

export const getVideoComments = async (
  videoId: string
): Promise<{ comments: Comment[]; status: CommentFetchStatus }> => {
  if (!videoId) return { comments: [], status: 'error' };
  if (getCachedUnavailable(videoId)) {
    return { comments: [], status: 'unavailable' };
  }

  logOnce(videoId, 'info', `Fetching comments for video ${videoId}`);

  try {
    const res = await withRateLimit(rateLimiters.comments, async () => {
      return axios.get(`${BASE_URL}/commentThreads`, {
        params: {
          part: 'snippet',
          videoId,
          key: getNextApiKey(),
          maxResults: 50,
          order: 'relevance',
        },
      });
    }, 1);

    const items = res?.data?.items || [];
    const parsed = items.map((item: any) => ({
      id: item.id,
      author: item.snippet.topLevelComment.snippet.authorDisplayName,
      text: item.snippet.topLevelComment.snippet.textDisplay,
      publishedAt: item.snippet.topLevelComment.snippet.publishedAt,
    }));

    return { comments: parsed, status: 'ok' };
  } catch (error) {
    const status = getHttpStatus(error);
    if (status === 400 || status === 403 || status === 404) {
      markUnavailable(videoId);
      logOnce(videoId, 'warn', `YouTube comments unavailable for ${videoId} (status ${status})`);
      return { comments: [], status: 'unavailable' };
    }

    logOnce(videoId, 'warn', `YouTube comments fetch failed for ${videoId}`);
    return { comments: [], status: 'error' };
  }
};

// --------- POST COMMENT ---------
export const postComment = async (
  videoId: string,
  commentText: string,
  accessToken: string
): Promise<void> => {
  logger.info(`Posting comment to video ${videoId}`);
  return withRetry(async () =>
    withRateLimit(rateLimiters.comments, async () => {
      try {
        await axios.post(
          `${BASE_URL}/commentThreads`,
          {
            snippet: {
              videoId,
              topLevelComment: {
                snippet: { textOriginal: commentText },
              },
            },
          },
          {
            params: {
              part: 'snippet',
              key: getNextApiKey(),
            },
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
      } catch (error) {
        throw handleAPIError(error);
      }
    }, 1)
  );
};

// --------- UTIL EXPORTS ---------
export { rateLimiters, withRateLimit, withRetry };
